import { AuthApiError } from '@/api/auth/emailAuth';

/**
 * Plain-language text for an auth API failure. The backend forwards some
 * Cognito/boto3 strings verbatim ("Invalid verification code", or a raw
 * "An error occurred (…) when calling the … operation" on a 500), always in
 * English — the same patterns the app maps in `app/src/auth/authApi.ts`
 * `friendlyAuthMessage`. Display only: callers keep their own status
 * routing (401, 403, 409) before falling back to this.
 */
export function friendlyAuthError(error: unknown, t: (key: string) => string, fallbackKey: string): string {
  if (!(error instanceof AuthApiError)) return t(fallbackKey);
  const m = error.message.toLowerCase();
  if (error.status === 0) return t('login.error.network');
  if (/invalid verification code|code\s*mismatch|codemismatch/.test(m)) return t('confirm.codeFail');
  if (/expired.*code|code.*expired|expiredcode/.test(m)) return t('auth.error.expiredCode');
  if (/limitexceeded|too many|attempt limit/.test(m)) return t('auth.error.tooMany');
  if (error.status >= 500 || /exception|traceback|when calling the/.test(m)) return t('login.error.unknown');
  return error.message || t(fallbackKey);
}

/**
 * A small text action on a field's label row ("Forgot password?", "Resend
 * code"): it keeps its compact look but gets a ≥ 44px hit area from an
 * invisible `::after`, without growing the row (ui-ux.md §9).
 */
export const LABEL_ACTION_HIT =
  "relative after:absolute after:-inset-x-2 after:-inset-y-3 after:content-['']";

/** Move focus to a field by id — the first invalid one after a failed submit. */
export function focusField(id: string) {
  document.getElementById(id)?.focus();
}
